import { z } from 'zod';
import { victimRiskPlanInputSchema, type VictimRiskPlanView } from './victim-risk-plan.js';

const id = z.string().min(1).max(128).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u);
const text = (maximum: number) => z.string().trim().min(1).max(maximum);
const optionalText = (maximum: number) => z.string().trim().max(maximum);
export const victimReportedPersonNameSchema = text(100).regex(/^[^\p{Cc}\d]+$/u, '재해자 이름만 입력해 주세요.');
const hireDate = z.union([z.literal(''), z.string().date()]).optional();
export const accidentCategorySchema = z.enum(['industrial', 'minor', 'property']);
export type AccidentCategory = z.infer<typeof accidentCategorySchema>;
export const accidentCategoryLabels: Record<AccidentCategory, string> = {
  industrial: '산재', minor: '경미상', property: '물적피해',
};

export const victimRegistryStatusSchema = z.enum([
  'draft',
  'submitted',
  'returned',
  'confirmed',
  'closed',
  'legacy-confirmed-observed',
  'legacy-closed-observed',
]);

export const victimWorkStatusSchema = z.enum([
  'under-treatment',
  'returned',
  'work-restricted',
  'separated',
]);

export const victimEvidenceReferenceSchema = z
  .object({ manifestId: id, sha256: z.string().regex(/^[a-f0-9]{64}$/u),
    caption: optionalText(300).optional(), role: z.enum(['scene', 'action']).optional() })
  .strict();

export const victimTrackingEventSchema = z
  .object({
    eventId: id,
    kind: z.enum([
      'treatment-update',
      'lost-days-update',
      'return-update',
      'prevention-update',
      'management-note',
    ]),
    observedAt: z.string().datetime(),
    note: text(1000),
    lostWorkDays: z.number().int().min(0).max(36500).nullable(),
    workStatus: victimWorkStatusSchema.nullable(),
    preventionConfirmed: optionalText(2000).nullable(),
    evidenceReferences: z.array(victimEvidenceReferenceSchema).max(20).optional(),
    createdBy: id,
    createdAt: z.string().datetime(),
  })
  .strict();

export const victimRegistryEntrySchema = z
  .object({
    entryId: id,
    version: z.number().int().positive(),
    tenantId: z.literal('kop'),
    siteId: id,
    contractorId: id,
    contractorName: text(120),
    accidentCategory: accidentCategorySchema.optional(),
    title: optionalText(200).optional(),
    damage: optionalText(1000).optional(),
    victimVaultRef: id.nullable(),
    maskedName: optionalText(40),
    occurredAt: z.string().datetime(),
    location: text(160),
    workDescription: text(1000),
    hireDate,
    incidentDetails: optionalText(4000).optional(),
    accidentType: text(80),
    ambulanceUsed: z.boolean(),
    currentSituation: text(1000),
    immediateActions: text(2000),
    riskReassessmentRequired: z.boolean(),
    riskAssessmentReference: optionalText(256),
    causeAnalysis: text(2000),
    behavioralCauses: optionalText(2000),
    facilityCauses: optionalText(2000),
    preventionDraft: text(2000),
    evidenceReferences: z.array(victimEvidenceReferenceSchema).max(20),
    injuryClassification: z.enum([
      'bruise',
      'cut',
      'fracture',
      'burn',
      'musculoskeletal',
      'other',
    ]).nullable(),
    injuryClassificationOther: optionalText(120).optional(),
    lostWorkDays: z.number().int().min(0).max(36500),
    workStatus: victimWorkStatusSchema.nullable(),
    preventionConfirmed: optionalText(2000),
    status: victimRegistryStatusSchema,
    managementStatus: z.enum(['in-progress', 'closed']).optional(),
    returnOpinion: optionalText(1000).nullable(),
    createdBy: id,
    createdAt: z.string().datetime(),
    updatedBy: id,
    updatedAt: z.string().datetime(),
    confirmedBy: id.nullable(),
    confirmedAt: z.string().datetime().nullable(),
    trackingEvents: z.array(victimTrackingEventSchema).max(500),
    migrationProvenance: z.object({
      sourceSystem: z.literal('legacy-safety-management'),
      sourceRecordHash: z.string().regex(/^[a-f0-9]{64}$/u),
      importedAt: z.string().datetime(),
      authoritativeApproval: z.literal(false),
      officialPrintReceipt: z.null(),
    }).strict().optional(),
    historicalPersonRef: id.optional(),
  })
  .strict().superRefine((value, context) => {
    if (value.accidentCategory === 'property') {
      if (value.victimVaultRef !== null || value.maskedName !== '' || value.injuryClassification !== null || value.workStatus !== null || value.lostWorkDays !== 0 || value.ambulanceUsed)
        context.addIssue({code: 'custom', message: '물적피해에는 재해자·치료 정보를 등록할 수 없습니다.'});
    } else if (!value.victimVaultRef || !value.maskedName || !value.injuryClassification || !value.workStatus) {
      context.addIssue({code: 'custom', message: '인명사고의 재해자 정보가 필요합니다.'});
    }
  });

export const victimRegistryEditableSchema = z
  .object({
    contractorId: id,
    riskReassessmentPlan: victimRiskPlanInputSchema.optional(),
    accidentCategory: accidentCategorySchema.optional(),
    title: optionalText(200).optional(),
    damage: optionalText(1000).optional(),
    victimVaultRef: id.nullable(),
    maskedName: optionalText(40),
    occurredAt: z.string().datetime(),
    location: text(160),
    workDescription: text(1000),
    hireDate,
    incidentDetails: optionalText(4000).optional(),
    accidentType: text(80),
    ambulanceUsed: z.boolean(),
    currentSituation: text(1000),
    immediateActions: text(2000),
    riskReassessmentRequired: z.boolean(),
    riskAssessmentReference: optionalText(256),
    causeAnalysis: text(2000),
    behavioralCauses: optionalText(2000),
    facilityCauses: optionalText(2000),
    preventionDraft: text(2000),
    evidenceReferences: z.array(victimEvidenceReferenceSchema).max(20),
    injuryClassification: z.enum([
      'bruise',
      'cut',
      'fracture',
      'burn',
      'musculoskeletal',
      'other',
    ]).nullable(),
    injuryClassificationOther: optionalText(120).optional(),
  })
  .strict();

function requireOtherInjuryText(value: { accidentCategory?: AccidentCategory; injuryClassification: string | null; injuryClassificationOther?: string; reportedPersonName?: string }, context: z.RefinementCtx) {
  if (value.accidentCategory === 'property') {
    if (value.injuryClassification !== null || value.reportedPersonName?.trim()) context.addIssue({code: 'custom', message: '물적피해에는 재해자 정보를 입력하지 않습니다.'});
    return;
  }
  if (value.injuryClassification === null) context.addIssue({code: 'custom', path: ['injuryClassification'], message: '재해 분류를 선택해 주세요.'});
  if (value.injuryClassification === 'other' && !value.injuryClassificationOther?.trim()) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['injuryClassificationOther'], message: '기타 재해 분류를 입력해 주세요.' });
  }
}

export const victimRegistryUpdateSchema = victimRegistryEditableSchema.omit({
  contractorId: true,
  victimVaultRef: true,
}).extend({ reportedPersonName: victimReportedPersonNameSchema.optional() }).strict().superRefine(requireOtherInjuryText);

/** A report author can name a victim without first obtaining an internal vault ID. */
export const victimRegistryNewReportSchema = victimRegistryEditableSchema.omit({
  victimVaultRef: true,
  maskedName: true,
}).extend({
  reportedPersonName: victimReportedPersonNameSchema.optional(),
}).strict().superRefine((value, context) => {
  requireOtherInjuryText(value, context);
  if (value.accidentCategory !== 'property' && !value.reportedPersonName)
    context.addIssue({code: 'custom', path: ['reportedPersonName'], message: '재해자 이름을 입력해 주세요.'});
});

export function maskVictimReportedName(name: string): string {
  const letters = [...name.trim()];
  return letters.length > 1 ? `${letters[0]}○○` : '○';
}

export const victimRegistryOperationSchema = z.enum([
  'victim.create',
  'victim.update',
  'victim.submit',
  'victim.resubmit',
  'victim.withdraw',
  'victim.return',
  'victim.confirm',
  'victim.close',
  'victim.delete',
  'victim.add-tracking',
]);

export const victimRegistryCommandSchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantId: z.literal('kop'),
    siteId: id,
    contractorId: id,
    entryId: id,
    operation: victimRegistryOperationSchema,
    expectedVersion: z.number().int().nonnegative(),
    commandId: id,
    idempotencyKey: id,
    payload: z.record(z.string(), z.unknown()),
  })
  .strict();

export type VictimRegistryEntry = z.infer<typeof victimRegistryEntrySchema>;
export type VictimRegistryEditable = z.infer<typeof victimRegistryEditableSchema>;
export type VictimRegistryUpdate = z.infer<typeof victimRegistryUpdateSchema>;
export type VictimRegistryOperation = z.infer<typeof victimRegistryOperationSchema>;
export type VictimRegistryCommand = z.infer<typeof victimRegistryCommandSchema>;

export type VictimRegistryAllowedAction =
  | 'edit'
  | 'submit'
  | 'resubmit'
  | 'withdraw-edit'
  | 'return'
  | 'confirm'
  | 'close'
  | 'print'
  | 'delete'
  | 'add-tracking';

export type VictimRegistryListItem = Readonly<{
  accidentCategory?: AccidentCategory;
  title?: string;
  damage?: string;
  evidenceReferences?: readonly z.infer<typeof victimEvidenceReferenceSchema>[];
  entryId: string;
  version: number;
  contractorId: string;
  contractorName: string;
  maskedName: string;
  reportedPersonName?: string;
  occurredAt: string;
  accidentType: string;
  location: string;
  injuryClassification: VictimRegistryEntry['injuryClassification'];
  injuryClassificationOther?: string;
  lostWorkDays: number;
  workStatus: VictimRegistryEntry['workStatus'];
  status: VictimRegistryEntry['status'];
  managementStatus?: 'in-progress' | 'closed';
  updatedAt: string;
  allowedActions: readonly VictimRegistryAllowedAction[];
}>;

export type VictimRiskReassessmentStatus = Readonly<{
  allowedActions: readonly ('create' | 'open')[];
  /** Server proof; the legacy free-text reference does not establish completion. */
  required: true;
  source: Readonly<{ entryId: string; sourceVersion: number; sourceHash: string }>;
  state: string;
  assessmentId: string | null;
  sourceChanged: boolean;
  complete: boolean;
}>;

export type VictimRegistryDetail = Omit<
  VictimRegistryEntry,
  'victimVaultRef' | 'createdBy' | 'updatedBy' | 'confirmedBy' | 'trackingEvents'
> & {
  reportedPersonName?: string;
  trackingEvents: readonly Omit<VictimRegistryEntry['trackingEvents'][number], 'createdBy'>[];
  allowedActions: readonly VictimRegistryAllowedAction[];
  riskReassessment?: VictimRiskReassessmentStatus;
  riskReassessmentPlan?: VictimRiskPlanView;
};

export const victimRegistryListOrderSchema = z.enum(['updatedAt', 'occurredAt']);
export const victimRegistryCursorSchema = z.union([
  z.object({ updatedAt: z.string().datetime(), entryId: id }).strict(),
  z.object({ orderBy: z.literal('occurredAt'), occurredAt: z.string().datetime(), entryId: id }).strict(),
]);
export type VictimRegistryListOrder = z.infer<typeof victimRegistryListOrderSchema>;
export type VictimRegistryCursor = z.infer<typeof victimRegistryCursorSchema>;

export function parseVictimRegistryListCursor(orderBy: VictimRegistryListOrder, input: unknown): VictimRegistryCursor | null {
  if (input === null || input === undefined) return null;
  const cursor = victimRegistryCursorSchema.parse(input);
  if ((orderBy === 'occurredAt') !== ('orderBy' in cursor)) throw new Error('VICTIM_CURSOR_ORDER_MISMATCH');
  return cursor;
}
type VictimRegistryPageBase = Readonly<{
  categorySummary?: Readonly<{year: number; industrial: number; minor: number; property: number; unclassified: number; total: number}>;
  heldCount?: number;
  source: 'server' | 'synthetic-review';
  items: readonly VictimRegistryListItem[];
  nextCursor: VictimRegistryCursor | null;
}>;

export type VictimRegistryPage = VictimRegistryPageBase & (
  | Readonly<{
      summaryIncluded: true;
      totalCount: number;
      summary: Readonly<{ submitted: number; underTreatment: number; closed: number }>;
    }>
  | Readonly<{
      summaryIncluded: false;
      totalCount: null;
      summary: null;
    }>
);
